import type { PixelEvent } from '@/events';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type {
  EngagementLevel,
  EngagementScoreBreakdown,
  EngagementSnapshot,
  EngagementUpdateReason,
  NormalizedEngagementConfig,
} from '../../contracts/engagement';
import type {
  ManagedScheduler as IManagedScheduler,
  SchedulerHandle,
} from '../../contracts/scheduler';
import type {
  BehaviorSignalBus,
  BehaviorSignalType,
} from '../../contracts/signals';
import { BehaviorSignalBus as SignalBusImpl } from '../../runtime/BehaviorSignalBus';
import { ManagedScheduler } from '../../runtime/ManagedScheduler';

import { EngagementEmissionPolicy } from './EngagementEmissionPolicy';
import { EngagementLevelResolver } from './EngagementLevelResolver';
import { EngagementScoreCalculator } from './EngagementScoreCalculator';
import { EngagementSignalAccumulator } from './EngagementSignalAccumulator';
import { EngagementSummaryBuilder } from './EngagementSummaryBuilder';

const CONSUMED_SIGNALS: readonly BehaviorSignalType[] = [
  'HEARTBEAT',
  'SCROLL',
  'CLICK',
  'RAGE_CLICK',
  'IDLE',
  'ACTIVITY',
  'VISIBLE',
  'HIDDEN',
  'VIEWPORT_ENTER',
  'VIEWPORT_EXIT',
];

export type EngagementEngineOptions = {
  readonly config: NormalizedEngagementConfig;
  readonly signalBus?: BehaviorSignalBus;
  /** Injeção opcional de scheduler (default: ManagedScheduler próprio). */
  readonly scheduler?: IManagedScheduler;
};

/**
 * Engagement Runtime (Etapa 19).
 *
 * Consome sinais internos do BehaviorSignalBus e produz um score determinístico
 * 0..100 via avaliação periódica (ManagedScheduler). Emite ENGAGEMENT_UPDATE e
 * ENGAGEMENT_SUMMARY pelo Dispatcher. Não lê DOM, não importa trackers, não usa
 * setInterval, funciona como no-op seguro em SSR.
 */
export class EngagementEngine {
  readonly id = 'engagement';

  private readonly context: PixelRuntimeContext;

  private readonly config: NormalizedEngagementConfig;

  private readonly signalBus: BehaviorSignalBus;

  private readonly ownsBus: boolean;

  private readonly scheduler: IManagedScheduler;

  private readonly ownsScheduler: boolean;

  private readonly accumulator = new EngagementSignalAccumulator();

  private readonly calculator: EngagementScoreCalculator;

  private readonly emissionPolicy: EngagementEmissionPolicy;

  private readonly summaryBuilder = new EngagementSummaryBuilder();

  private initialized = false;

  private running = false;

  private destroyed = false;

  private hasError = false;

  private tickHandle: SchedulerHandle | null = null;

  private unsubscribers: Array<() => void> = [];

  private score = 0;

  private lastEmittedScore = 0;

  private peakScore = 0;

  private scoreSum = 0;

  private evaluationCount = 0;

  private emittedUpdateCount = 0;

  private emittedSummaryCount = 0;

  private level: EngagementLevel = 'low';

  private lastEvaluatedAt: number | undefined;

  private lastEmittedAt: number | undefined;

  constructor(context: PixelRuntimeContext, options: EngagementEngineOptions) {
    this.context = context;
    this.config = options.config;

    if (options.signalBus) {
      this.signalBus = options.signalBus;
      this.ownsBus = false;
    } else {
      this.signalBus = new SignalBusImpl();
      this.ownsBus = true;
    }

    if (options.scheduler) {
      this.scheduler = options.scheduler;
      this.ownsScheduler = false;
    } else {
      this.scheduler = new ManagedScheduler();
      this.ownsScheduler = true;
    }

    const levelResolver = new EngagementLevelResolver(this.config.thresholds);
    this.calculator = new EngagementScoreCalculator(this.config, levelResolver);
    this.emissionPolicy = new EngagementEmissionPolicy(this.config);
  }

  init(): void {
    if (this.initialized && !this.destroyed) {
      return;
    }

    const now = Date.now();
    this.destroyed = false;
    this.hasError = false;
    this.accumulator.reset(now);
    // Bus próprio é limpo (cleared) no destroy; reset() reabilita subscribe()
    // no reinit. Bus injetado pertence ao BehaviorModule e nunca é resetado aqui.
    if (this.ownsBus && this.signalBus instanceof SignalBusImpl) {
      this.signalBus.reset();
    }
    if (this.ownsScheduler && this.scheduler instanceof ManagedScheduler) {
      this.scheduler.reset();
    }
    this.score = 0;
    this.lastEmittedScore = 0;
    this.peakScore = 0;
    this.scoreSum = 0;
    this.evaluationCount = 0;
    this.emittedUpdateCount = 0;
    this.emittedSummaryCount = 0;
    this.level = 'low';
    this.lastEvaluatedAt = undefined;
    this.lastEmittedAt = undefined;
    this.tickHandle = null;

    this.bindSignals();

    this.initialized = true;
    this.running = true;

    // Emissão inicial documentada (baseline).
    this.evaluate('initial');
    this.scheduleNext();
  }

  destroy(): void {
    if (this.destroyed && !this.initialized) {
      return;
    }

    const wasInitialized = this.initialized;

    this.running = false;
    this.destroyed = true;
    this.cancelTick();

    if (wasInitialized && this.config.emitSummaryOnDestroy) {
      this.emitSummary();
    }

    this.unbindSignals();

    if (this.ownsScheduler) {
      this.scheduler.destroy();
    }
    if (this.ownsBus && this.signalBus instanceof SignalBusImpl) {
      this.signalBus.clear();
    }

    this.initialized = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  isRunning(): boolean {
    return this.running;
  }

  /** Avaliação manual (playground/testes). Não reprograma o intervalo. */
  evaluateNow(): EngagementScoreBreakdown | null {
    if (!this.running || this.destroyed) return null;
    return this.evaluate('manual_evaluation');
  }

  activeTimerCount(): number {
    return this.scheduler.activeCount();
  }

  /**
   * @internal Publica um sinal no bus do engine (playground/testes). Útil para
   * exercitar o caminho do bus próprio (não injetado) após reinit.
   */
  debugPublishSignal(
    type: BehaviorSignalType,
    data?: Parameters<BehaviorSignalBus['createSignal']>[2]
  ): void {
    this.signalBus.publish(this.signalBus.createSignal(type, 'debug', data));
  }

  getSnapshot(): EngagementSnapshot {
    return {
      enabled: this.config.enabled,
      initialized: this.initialized,
      running: this.running,
      score: this.score,
      previousScore: this.lastEmittedScore,
      peakScore: this.peakScore,
      averageScore:
        this.evaluationCount > 0
          ? Math.round(this.scoreSum / this.evaluationCount)
          : 0,
      level: this.level,
      evaluationCount: this.evaluationCount,
      emittedUpdateCount: this.emittedUpdateCount,
      emittedSummaryCount: this.emittedSummaryCount,
      lastEvaluatedAt: this.lastEvaluatedAt,
      lastEmittedAt: this.lastEmittedAt,
      evaluationIntervalMs: this.config.evaluationIntervalMs,
      lastDriftMs: this.scheduler.lastDriftMs(),
      hasError: this.hasError,
      signalCounts: this.accumulator.getSignalCounts(),
    };
  }

  private bindSignals(): void {
    this.unbindSignals();
    for (const type of CONSUMED_SIGNALS) {
      this.unsubscribers.push(
        this.signalBus.subscribe(type, signal => {
          if (this.destroyed || !this.running) return;
          this.accumulator.ingest(signal);
        })
      );
    }
  }

  private unbindSignals(): void {
    for (const unsub of this.unsubscribers) {
      try {
        unsub();
      } catch {
        // ignore
      }
    }
    this.unsubscribers = [];
  }

  private scheduleNext(): void {
    this.cancelTick();
    if (this.destroyed || !this.running) return;
    this.tickHandle = this.scheduler.schedule(
      () => this.tick(),
      this.config.evaluationIntervalMs
    );
  }

  private cancelTick(): void {
    if (this.tickHandle) {
      this.scheduler.cancel(this.tickHandle);
      this.tickHandle = null;
    }
  }

  private tick(): void {
    this.tickHandle = null;
    if (this.destroyed || !this.running) return;
    this.evaluate('interval');
    this.scheduleNext();
  }

  private evaluate(
    reason: EngagementUpdateReason
  ): EngagementScoreBreakdown | null {
    const now = Date.now();
    this.accumulator.flush(now);

    let breakdown: EngagementScoreBreakdown;
    try {
      breakdown = this.calculator.calculate(this.accumulator.getState());
    } catch {
      // Falha isolada do calculator não derruba o engine (health degraded).
      this.hasError = true;
      this.context.logger.warn(
        '[Pixel] Engagement score calculation failed (isolated)'
      );
      return null;
    }

    this.evaluationCount += 1;
    this.score = breakdown.finalScore;
    this.level = breakdown.level;
    this.scoreSum += breakdown.finalScore;
    if (breakdown.finalScore > this.peakScore) {
      this.peakScore = breakdown.finalScore;
    }
    this.lastEvaluatedAt = now;

    const shouldEmit = this.emissionPolicy.shouldEmit({
      reason,
      score: breakdown.finalScore,
      previousScore: this.lastEmittedScore,
    });

    if (shouldEmit) {
      this.emitUpdate(breakdown.finalScore, breakdown.level, reason, now);
    }

    return breakdown;
  }

  private emitUpdate(
    score: number,
    level: EngagementLevel,
    reason: EngagementUpdateReason,
    evaluatedAt: number
  ): void {
    const previousScore = this.lastEmittedScore;
    const delta = score - previousScore;

    try {
      const event = this.context.services.eventBuilder.build({
        type: 'ENGAGEMENT_UPDATE',
        category: 'ENGAGEMENT',
        score,
        previousScore,
        delta,
        level,
        evaluatedAt,
        reason,
      }) as PixelEvent;

      this.context.services.dispatcher.dispatch(event);
      this.lastEmittedScore = score;
      this.emittedUpdateCount += 1;
      this.lastEmittedAt = evaluatedAt;
    } catch {
      this.context.logger.warn(
        '[Pixel] Engagement update emit failed (listener isolated)'
      );
    }
  }

  private emitSummary(): void {
    if (this.emittedSummaryCount > 0) return;

    const now = Date.now();
    this.accumulator.flush(now);
    const state = this.accumulator.getState();
    const averageScore =
      this.evaluationCount > 0
        ? Math.round(this.scoreSum / this.evaluationCount)
        : 0;

    const summary = this.summaryBuilder.build({
      state,
      score: this.score,
      peakScore: this.peakScore,
      averageScore,
      level: this.level,
      endedAt: now,
    });

    try {
      const event = this.context.services.eventBuilder.build({
        type: 'ENGAGEMENT_SUMMARY',
        category: 'ENGAGEMENT',
        ...summary,
      }) as PixelEvent;

      this.context.services.dispatcher.dispatch(event);
      this.emittedSummaryCount += 1;
    } catch {
      // Emissão de summary no destroy é best-effort; marca como emitido para
      // garantir "no máximo uma vez" mesmo se o Dispatcher já estiver indisponível.
      this.emittedSummaryCount += 1;
      this.context.logger.warn(
        '[Pixel] Engagement summary emit failed (listener isolated)'
      );
    }
  }
}
